import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DEFECT_TYPES, DEFECT_STATUS, SEVERITY, formatDate, formatEUR } from "@/lib/dict";
import { Wrench, CheckCircle2, Clock, PlayCircle } from "lucide-react";
import { toast } from "sonner";

export default function ContractorPortal() {
  const qc = useQueryClient();
  const { data: defects = [] } = useQuery({ queryKey: ["contractor-defects"], queryFn: () => api.get("/contractor/defects").then((r) => r.data) });
  const update = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/defects/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contractor-defects"] }); toast.success("Statut mis à jour"); },
  });

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#023E73", color: "white" }}><Wrench size={22} /></div>
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Portail entreprise</h1>
          <p className="text-sm text-slate-500">{defects.length} intervention(s) assignée(s)</p>
        </div>
      </div>

      <div className="space-y-3">
        {defects.map((d) => (
          <Card key={d.id} data-testid={`contractor-defect-${d.id}`} className="p-5 border-slate-200">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="font-mono text-slate-400">{d.reference}</span>
                  <span className={`px-2 py-0.5 rounded-full font-semibold border ${SEVERITY[d.severity]?.color}`}>{SEVERITY[d.severity]?.label}</span>
                </div>
                <div className="font-display font-semibold text-lg text-slate-900 mt-1">{d.title}</div>
                <div className="text-xs text-slate-500 mt-1">{DEFECT_TYPES[d.type]} • Observé le {formatDate(d.date_observed)}</div>
                {d.description && <p className="text-sm text-slate-600 mt-2">{d.description}</p>}
              </div>
              <div className="text-right">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DEFECT_STATUS[d.status]?.color}`}>{DEFECT_STATUS[d.status]?.label}</span>
                <div className="text-xs text-slate-500 mt-2">Estimé : <span className="font-mono font-semibold">{formatEUR(d.estimated_cost)}</span></div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {d.status === "assigned" && <Button data-testid={`accept-${d.id}`} size="sm" className="text-white" style={{ background: "#023E73" }} onClick={() => update.mutate({ id: d.id, status: "accepted" })}><CheckCircle2 size={14} className="mr-1.5" /> Accepter</Button>}
              {["assigned", "accepted"].includes(d.status) && <Button data-testid={`start-${d.id}`} size="sm" variant="outline" onClick={() => update.mutate({ id: d.id, status: "in_progress" })}><PlayCircle size={14} className="mr-1.5" /> Démarrer</Button>}
              {["in_progress", "follow_up"].includes(d.status) && <Button data-testid={`complete-${d.id}`} size="sm" className="text-white bg-emerald-600 hover:bg-emerald-700" onClick={() => update.mutate({ id: d.id, status: "completed" })}><CheckCircle2 size={14} className="mr-1.5" /> Marquer terminé</Button>}
              {d.status === "in_progress" && <Button data-testid={`followup-${d.id}`} size="sm" variant="outline" onClick={() => update.mutate({ id: d.id, status: "follow_up" })}><Clock size={14} className="mr-1.5" /> Relance / Complément</Button>}
            </div>
          </Card>
        ))}
        {defects.length === 0 && <div className="text-sm text-slate-400 text-center py-12">Aucune intervention assignée</div>}
      </div>
    </div>
  );
}
